import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import Sidebar from "@/components/Sidebar";
import axios from "axios";
import { authAxios as ax } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CalendarDays, Download, FileText, BarChart3, Phone, Clock, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const langLabels = { en: "English", es: "Spanish", fr: "French", ht: "Haitian Creole" };
const sentimentColors = { positive: "#00C853", neutral: "#FFB800", negative: "#FF2A2A" };
const PIE_COLORS = ["#002FA7", "#0A0A0A", "#00C853", "#FFB800"];

function DatePicker({ label, date, onSelect }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="rounded-none h-9 text-xs font-medium justify-start min-w-[140px]" data-testid={`date-picker-${label.toLowerCase()}`}>
          <CalendarDays size={14} className="mr-2 text-[#9CA3AF]" />
          {date ? format(date, "yyyy-MM-dd") : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 rounded-none" align="start">
        <Calendar mode="single" selected={date} onSelect={onSelect} />
      </PopoverContent>
    </Popover>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-5 flex items-start gap-4">
      <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: accent }}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-2xl font-black text-[#0A0A0A] tracking-tight">{value}</p>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#9CA3AF] font-bold mt-0.5">{label}</p>
      </div>
    </div>
  );
}

export default function ReportingPage() {
  const { t, language } = useLanguage();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = format(startDate, "yyyy-MM-dd");
      if (endDate) params.end_date = format(endDate, "yyyy-MM-dd");
      const res = await ax.get(`${API}/reports/analytics`, { params });
      setData(res.data);
    } catch {
      toast.error("Failed to load report data");
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const exportFile = async (type) => {
    setExporting(type);
    try {
      const params = { lang: language };
      if (startDate) params.start_date = format(startDate, "yyyy-MM-dd");
      if (endDate) params.end_date = format(endDate, "yyyy-MM-dd");
      const res = await ax.get(`${API}/reports/export/${type}`, {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `talk2support_report.${type}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} exported`);
    } catch {
      toast.error(`${type.toUpperCase()} export failed`);
    }
    setExporting("");
  };

  const sentimentData = data ? Object.entries(data.sentiment_distribution || {}).map(([k, v]) => ({
    name: t(`client.${k}`), value: v, fill: sentimentColors[k]
  })) : [];

  const langData = data ? Object.entries(data.language_distribution || {}).map(([k, v], i) => ({
    name: langLabels[k] || k, value: v, fill: PIE_COLORS[i % PIE_COLORS.length]
  })) : [];

  return (
    <div className="flex h-screen bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] tracking-tight" data-testid="reports-title">
              {t("reporting.title")}
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <DatePicker label={t("reporting.from")} date={startDate} onSelect={setStartDate} />
              <span className="text-[#9CA3AF] text-xs font-bold">-</span>
              <DatePicker label={t("reporting.to")} date={endDate} onSelect={setEndDate} />
              <Button
                className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white h-9 text-xs font-bold tracking-[0.1em] uppercase"
                onClick={loadData}
                disabled={loading}
                data-testid="generate-report-btn"
              >
                {t("reporting.generate")}
              </Button>
            </div>
          </div>

          {/* Export buttons */}
          <div className="flex gap-2 mb-6">
            <Button
              variant="outline"
              className="rounded-none h-9 text-xs font-bold tracking-[0.1em] uppercase"
              onClick={() => exportFile("csv")}
              disabled={!!exporting}
              data-testid="export-csv-btn"
            >
              <Download size={14} className="mr-1" /> {exporting === "csv" ? t("common.loading") : t("reporting.export_csv")}
            </Button>
            <Button
              variant="outline"
              className="rounded-none h-9 text-xs font-bold tracking-[0.1em] uppercase"
              onClick={() => exportFile("pdf")}
              disabled={!!exporting}
              data-testid="export-pdf-btn"
            >
              <FileText size={14} className="mr-1" /> {exporting === "pdf" ? t("common.loading") : t("reporting.export_pdf")}
            </Button>
          </div>

          {data ? (
            <>
              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard icon={Phone} label={t("reporting.total_calls")} value={data.summary.total_calls} accent="#002FA7" />
                <StatCard icon={Clock} label={t("reporting.avg_handle_time")} value={`${data.summary.avg_handle_time}m`} accent="#0A0A0A" />
                <StatCard icon={TrendingUp} label={t("reporting.avg_satisfaction")} value={`${data.summary.avg_satisfaction}/5`} accent="#00C853" />
                <StatCard icon={BarChart3} label={t("reporting.sla_compliance")} value={`${data.summary.avg_sla_compliance}%`} accent="#FFB800" />
              </div>

              {/* Charts */}
              <div className="grid lg:grid-cols-2 gap-6 mb-8">
                <div className="border border-[#E5E7EB] bg-white p-6" data-testid="report-sentiment-chart">
                  <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A] mb-4">{t("reporting.by_sentiment")}</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={sentimentData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="value" radius={0}>
                        {sentimentData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="border border-[#E5E7EB] bg-white p-6" data-testid="report-language-chart">
                  <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A] mb-4">{t("reporting.by_language")}</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={langData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {langData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Projects Table */}
              <div className="border border-[#E5E7EB] bg-white mb-8" data-testid="report-projects-table">
                <div className="p-4 border-b border-[#E5E7EB]">
                  <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A]">{t("reporting.by_project")}</h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-[#0A0A0A]/80">
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("common.name")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("admin.active_ops")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("admin.avg_handle_time")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("admin.sla_compliance")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("admin.calls_today")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.projects.map((p) => (
                      <TableRow key={p.project_id} className="border-b border-[#E5E7EB]">
                        <TableCell className="py-3 font-semibold text-sm">{p.name}</TableCell>
                        <TableCell className="py-3 text-sm">{p.active_operators}</TableCell>
                        <TableCell className="py-3 text-sm">{p.avg_handle_time}m</TableCell>
                        <TableCell className="py-3">
                          <span className={`font-bold ${p.sla_compliance >= 90 ? "text-[#00C853]" : p.sla_compliance >= 80 ? "text-[#FFB800]" : "text-[#FF2A2A]"}`}>
                            {p.sla_compliance}%
                          </span>
                        </TableCell>
                        <TableCell className="py-3 text-sm">{p.calls_today}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Recordings Table */}
              <div className="border border-[#E5E7EB] bg-white" data-testid="report-recordings-table">
                <div className="p-4 border-b border-[#E5E7EB]">
                  <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#0A0A0A]">{t("client.recordings_title")}</h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-[#0A0A0A]/80">
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">Operator</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("client.language")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("client.duration")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("client.sentiment")}</TableHead>
                      <TableHead className="text-[10px] tracking-[0.2em] uppercase font-bold py-3">{t("client.satisfaction")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.recordings.slice(0, 20).map((r) => (
                      <TableRow key={r.recording_id} className="border-b border-[#E5E7EB]">
                        <TableCell className="py-2 text-sm">{r.operator_name}</TableCell>
                        <TableCell className="py-2"><Badge variant="outline" className="rounded-none text-[10px] font-bold">{r.caller_language}</Badge></TableCell>
                        <TableCell className="py-2 text-sm">{Math.floor(r.duration_seconds / 60)}:{String(r.duration_seconds % 60).padStart(2, "0")}</TableCell>
                        <TableCell className="py-2"><span className="text-xs font-bold" style={{ color: sentimentColors[r.sentiment] }}>{r.sentiment}</span></TableCell>
                        <TableCell className="py-2 text-sm font-bold">{r.satisfaction_score}/5</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}
